import { useHasEnabledFeatures } from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useVrpRetoolQuery } from '@vrplatform/data';
import { lazyWithRetry } from './_utils';

const FeaturesView = lazyWithRetry(() => import('../../views/features'));
const SettingsView = lazyWithRetry(() => import('../../views/settings'));
const AutomationsView = lazyWithRetry(
  () => import('../../views/partner-automations')
);
const BillingView = lazyWithRetry(() => import('../../views/vrp-billing'));
const UsersView = lazyWithRetry(() => import('../../views/vrp-users'));
const BookingChannelsView = lazyWithRetry(
  () => import('../../views/vrp-booking-channels')
);
const TeamsView = lazyWithRetry(() => import('../../views/vrp-teams'));
const ConnectionsView = lazyWithRetry(
  () => import('../../views/partner-connections')
);
const MembersView = lazyWithRetry(() => import('../../views/partner-members'));
const OwnerStatementsView = lazyWithRetry(
  () => import('../../views/partner-statements')
);
const AccountsView = lazyWithRetry(() => import('../../views/accounts'));
const VrpPartnerLineMappingsView = lazyWithRetry(
  () => import('../../views/accounts/VrpPartnerLineMappingsView')
);
const LegacyReportsView = lazyWithRetry(
  () => import('../../views/reports/ReportsView')
);
const RetoolView = lazyWithRetry(() => import('../../views/retool/RetoolView'));
const DashboardView = lazyWithRetry(
  () => import('../../views/partner-dashboard')
);

const StatementLayoutsView = lazyWithRetry(
  () => import('../../views/statements/layouts')
);

const FeeRoutes = lazyWithRetry(() => import('../../views/fees/FeesRoutes'));
const FeeEditView = lazyWithRetry(
  () => import('../../views/fees/edit/FeeEditView')
);

export const VrpRoutes = () => {
  const hasEnabledFeatures = useHasEnabledFeatures();

  const { data: vrpRetoolViews } = useVrpRetoolQuery();

  return (
    <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
      <Routes>
        <Route path="dashboard">
          <Route
            index
            element={<Navigate to="/dashboard/overview" replace />}
          />
          <Route path=":tabValue" element={<DashboardView />} />
        </Route>

        <Route
          path="property-manager"
          element={<TeamsView type="propertyManager" />}
        />
        <Route path="partner" element={<TeamsView type="partner" />} />
        <Route path="users" element={<UsersView />} />
        <Route path="booking-channels" element={<BookingChannelsView />} />

        <Route path="billing">
          <Route index element={<Navigate to="/billing/teams" replace />} />
          <Route path=":tabValue" element={<BillingView />} />
        </Route>

        <Route path="accounts">
          <Route index element={<Navigate to="/accounts/overview" replace />} />
          <Route path=":tabValue" element={<AccountsView />} />
        </Route>
        <Route path="line-mappings">
          <Route
            index
            element={<Navigate to="/line-mappings/transactions" replace />}
          />
          <Route path=":tabValue" element={<VrpPartnerLineMappingsView />} />
        </Route>

        <Route path="fees-commission/:id/edit" element={<FeeEditView />} />
        <Route path="fees-commissions">
          <Route index element={<FeeRoutes />} />
          <Route path="*" element={<FeeRoutes />} />
        </Route>

        <Route
          path="statements/templates"
          element={<Navigate to="/statements/templates/default" replace />}
        />
        <Route
          path="statements/templates/:tabValue"
          element={<StatementLayoutsView />}
        />

        {!!vrpRetoolViews?.length && (
          <Route path="retool">
            <Route
              index
              element={
                <Navigate to={`/retool/${vrpRetoolViews[0].id}`} replace />
              }
            />
            {vrpRetoolViews.map((view) => (
              <Route
                key={view.id}
                path={view.id}
                element={<RetoolView title={view.title} id={view.id} />}
              />
            ))}
          </Route>
        )}

        <Route path="members" element={<MembersView />} />

        <Route path="team-statements">
          <Route
            index
            element={
              <Navigate to="/team-statements/owner-statements" replace />
            }
          />
          <Route path=":tabValue" element={<OwnerStatementsView />} />
        </Route>

        <Route path="team-automations">
          <Route
            index
            element={<Navigate to="/team-automations/overview" replace />}
          />
          <Route path=":tabValue" element={<AutomationsView />} />
        </Route>

        <Route path="team-connections">
          <Route index element={<ConnectionsView />} />
          <Route path=":tabValue" element={<ConnectionsView />} />
        </Route>

        {hasEnabledFeatures && (
          <Route path="features">
            <Route path=":tabValue" element={<FeaturesView />} />
            <Route index element={<Navigate to="features" replace />} />
          </Route>
        )}

        <Route path="reports">
          <Route index element={<LegacyReportsView />} />
          <Route path=":id" element={<LegacyReportsView />} />
        </Route>

        {/* user settings */}
        <Route path="settings/*" element={<SettingsView />} />

        {/* catch all */}
        <Route path="*" element={<Navigate to="/property-manager" replace />} />
      </Routes>
    </Suspense>
  );
};
